// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Tracing helpers — mirrors Python provide.telemetry @trace decorator and tracer access.
 *
 * Uses @opentelemetry/api which provides no-op implementations when no SDK is registered.
 * withTrace() / @trace work safely without any OTEL setup; they just don't export spans.
 */
import { SpanStatusCode, trace, context as otelContext, } from '@opentelemetry/api';
import { _emittedField, _incrementHealth } from './health';
import { getActiveOtelContext } from './propagation';
import { randomHex } from './hash';
import { shouldAllow } from './consent';
import { shouldSample } from './sampling';
import { tryAcquire, release } from './backpressure';
import { getConfig } from './config';
// Stryker disable next-line StringLiteral: tracer name is not observable without a real SDK
const TRACER_NAME = '@provide-io/telemetry';
let _als = null;
try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { AsyncLocalStorage } = require('node:async_hooks');
    _als = new AsyncLocalStorage();
    // Stryker disable next-line BlockStatement: catch body sets the browser/Deno
    // fallback (`_als = null`) — Node-based tests cannot reach this branch.
}
catch {
    // Non-Node runtime (browser, Deno): fall back to module globals.
    /* c8 ignore next */
    _als = null;
}
let _manualTraceId;
let _manualSpanId;
/**
 * Manually inject trace/span IDs (e.g. from an incoming request header).
 * Returned by getTraceContext(); cleared by _resetTraceContext().
 * Pass undefined (or empty string) for either argument to clear that ID.
 */
export function setTraceContext(traceId, spanId) {
    // Treat empty strings as clearing the value — prevents empty-string IDs in logs.
    const normalizedTraceId = traceId || undefined;
    const normalizedSpanId = spanId || undefined;
    // Stryker disable next-line OptionalChaining: `_als?.` collapses to `_als.` only when _als is null (browser/Deno fallback) — Node tests can't enter that branch
    const store = _als?.getStore();
    if (store !== undefined) {
        store.traceId = normalizedTraceId;
        store.spanId = normalizedSpanId;
        return;
    }
    _manualTraceId = normalizedTraceId;
    _manualSpanId = normalizedSpanId;
}
/**
 * Return the current trace context: manual injection first, then active OTEL span.
 */
export function getTraceContext() {
    // Stryker disable next-line OptionalChaining: `_als?.` collapses to `_als.` only when _als is null (browser/Deno fallback) — Node tests can't enter that branch
    const store = _als?.getStore();
    const traceId = store?.traceId ?? _manualTraceId;
    const spanId = store?.spanId ?? _manualSpanId;
    // Stryker disable next-line ConditionalExpression,LogicalOperator: setTraceContext always sets both; partial state not reachable via public API
    if (traceId !== undefined || spanId !== undefined) {
        return {
            // Stryker disable next-line ConditionalExpression: traceId is always defined here (both set together)
            ...(traceId !== undefined && { trace_id: traceId }),
            // Stryker disable next-line ConditionalExpression: spanId is always defined here (both set together)
            ...(spanId !== undefined && { span_id: spanId }),
        };
    }
    const ids = getActiveTraceIds();
    return {
        ...(ids.trace_id !== undefined && { trace_id: ids.trace_id }),
        ...(ids.span_id !== undefined && { span_id: ids.span_id }),
    };
}
/** Reset manually injected trace context (used in tests). */
export function _resetTraceContext() {
    _manualTraceId = undefined;
    _manualSpanId = undefined;
    // Stryker disable next-line OptionalChaining: `_als?.` collapses to `_als.` only when _als is null (browser/Deno fallback) — Node tests can't enter that branch
    const store = _als?.getStore();
    if (store !== undefined) {
        store.traceId = undefined;
        store.spanId = undefined;
    }
}
/** Return the tracer for the telemetry library. Noop when no SDK is registered. */
export function getTracer() {
    return trace.getTracer(TRACER_NAME);
}
/** Module-level lazy singleton tracer — resolves provider at call time. */
export const tracer = trace.getTracer(TRACER_NAME);
/**
 * Return trace_id and span_id from the currently active OTEL span.
 * Returns an empty object when no span is active or OTEL is not configured.
 */
export function getActiveTraceIds() {
    const span = trace.getActiveSpan();
    if (!span)
        return {};
    const ctx = span.spanContext();
    // OTEL no-op spans have all-zero IDs — treat as no active span.
    if (ctx.traceId === '00000000000000000000000000000000')
        return {};
    return { trace_id: ctx.traceId, span_id: ctx.spanId };
}
const NOOP_TRACE_ID = '00000000000000000000000000000000';
/**
 * Return true if the given span is a no-op (all-zero trace ID).
 * Used to decide whether to inject synthetic random IDs.
 */
function _isNoopSpan(span) {
    // Stryker disable next-line ConditionalExpression: without a registered OTel SDK all spans are noop — mutating to true is equivalent
    return span.spanContext().traceId === NOOP_TRACE_ID;
}
/**
 * Execute fn with random synthetic trace/span IDs set as manual context.
 *
 * On Node we open a fresh AsyncLocalStorage scope so concurrent callers never
 * see each other's IDs: each await chain carries its own store.  Outside Node
 * we fall back to the save/restore pattern on module globals.
 */
function _withSyntheticIds(fn) {
    // Stryker disable next-line StringLiteral: random IDs are non-deterministic — exact value not observable in mutations
    const traceId = randomHex(16);
    const spanId = randomHex(8);
    /* v8 ignore start */
    // Stryker disable next-line ConditionalExpression: `if (true)` mutant equivalent in Node.js — _als is never null in tests
    if (_als !== null) {
        return _als.run({ traceId, spanId }, fn);
    }
    /* v8 ignore stop */
    /* c8 ignore start -- browser/Deno fallback: _als is always non-null in Node.js tests */
    const prevTraceId = _manualTraceId;
    const prevSpanId = _manualSpanId;
    _manualTraceId = traceId;
    _manualSpanId = spanId;
    const result = fn();
    // Stryker disable next-line ConditionalExpression: reachable only in the
    // browser/Deno fallback where _als is null, which Node-based tests cannot
    // enter without a source-level seam into _als. The sync-vs-Promise branch
    // is covered end-to-end by the Node path; pinning here requires mutating
    // a module-private for no gain.
    if (result instanceof Promise) {
        return result.then((value) => {
            _manualTraceId = prevTraceId;
            _manualSpanId = prevSpanId;
            return value;
        }, (err) => {
            _manualTraceId = prevTraceId;
            _manualSpanId = prevSpanId;
            throw err;
        });
    }
    _manualTraceId = prevTraceId;
    _manualSpanId = prevSpanId;
    return result;
    /* c8 ignore stop */
}
/** Shared span handler for withTrace — records exceptions and sets ERROR status. */
function _spanHandler(fn, span) {
    _incrementHealth(_emittedField('traces'));
    try {
        const result = fn();
        if (result instanceof Promise) {
            return result.then((value) => {
                span.end();
                return value;
            }, (err) => {
                span.recordException(err instanceof Error ? err : new Error(String(err)));
                span.setStatus({ code: SpanStatusCode.ERROR, message: String(err) });
                span.end();
                throw err;
            });
        }
        span.end();
        return result;
    }
    catch (err) {
        span.recordException(err instanceof Error ? err : new Error(String(err)));
        span.setStatus({ code: SpanStatusCode.ERROR, message: String(err) });
        span.end();
        throw err;
    }
}
/**
 * Execute fn inside a new span named `name`.
 * Works for both sync and async functions.
 * Mirrors Python @trace decorator behaviour: records exceptions, sets ERROR status.
 */
export function withTrace(name, fn) {
    if (!getConfig().tracingEnabled)
        return fn();
    // Stryker disable next-line StringLiteral: 'traces' vs '' is equivalent — shouldAllow treats any non-'logs'/non-'context' signal identically across all consent levels
    if (!shouldAllow('traces'))
        return fn();
    if (!shouldSample('traces', name))
        return fn();
    // Stryker disable next-line StringLiteral: 'traces' vs '' — both return a ticket when all queues are unbounded (default); differentiating requires a bounded-only-traces test that checks tracesEmitted
    const ticket = tryAcquire('traces');
    if (!ticket)
        return fn();
    const tracer = trace.getTracer(TRACER_NAME);
    // If an OTel context was extracted from propagation headers, use it as parent.
    // Narrow the try/catch to getActiveOtelContext() only so that errors inside
    // otelContext.with() propagate naturally instead of being swallowed and causing
    // a second span attempt in the fallback path without holding a backpressure slot.
    let activeCtx;
    try {
        activeCtx = getActiveOtelContext();
    }
    catch {
        // getActiveOtelContext() threw — graceful degradation, fall through to default behaviour.
    }
    if (activeCtx) {
        try {
            return otelContext.with(activeCtx, () => tracer.startActiveSpan(name, (span) => {
                // Stryker disable next-line ConditionalExpression: noop detection is not observable without SDK — branch outcome equivalent under mutation
                /* v8 ignore start: noop-span false branch + real-span return are unreachable without a registered OTel provider */
                if (_isNoopSpan(span))
                    return _withSyntheticIds(() => _spanHandler(fn, span));
                return _spanHandler(fn, span);
                /* v8 ignore stop */
            }));
        }
        finally {
            release(ticket);
        }
    }
    try {
        return tracer.startActiveSpan(name, (span) => {
            // Stryker disable next-line ConditionalExpression: noop detection is not observable without SDK — branch outcome equivalent under mutation
            if (_isNoopSpan(span))
                return _withSyntheticIds(() => _spanHandler(fn, span));
            return _spanHandler(fn, span);
        });
    }
    finally {
        release(ticket);
    }
}
/**
 * Method/function decorator that wraps the target in withTrace().
 * Span name defaults to the decorated method name.
 *
 * Usage (requires experimentalDecorators: true):
 *   class Foo {
 *     @trace('my.operation')
 *     doWork() { ... }
 *   }
 */
// Stryker disable BlockStatement: outer and inner decorator fns returning undefined leave descriptors unchanged — equivalent
export function traceDecorator(name) {
    return function (_target, propertyKey, descriptor) {
        // Stryker enable BlockStatement
        // Stryker disable next-line LogicalOperator: span name not observable with no-op tracer
        const spanName = name ?? String(propertyKey);
        const original = descriptor.value;
        descriptor.value = function (...args) {
            return withTrace(spanName, () => original.apply(this, args));
        };
        return descriptor;
    };
}
